import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { config } from '../config';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  gradeLevel: number;
  status: string;
  parentName: string;
  parentEmail: string;
  enrollmentDate: string;
  programName: string;
}

interface AttendanceRecord {
  id: string;
  studentName: string;
  programName: string;
  date: string;
  status: 'present' | 'absent' | 'late';
  notes?: string;
}

interface ClassStats {
  totalStudents: number;
  presentToday: number;
  absentToday: number;
  lateToday: number;
  averageAttendance: number;
  upcomingSessions: number;
}

const TeacherDashboard: React.FC = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [classStats, setClassStats] = useState<ClassStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'students' | 'attendance' | 'classes' | 'reports'>('overview');
  const [showAttendanceForm, setShowAttendanceForm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Get local date in YYYY-MM-DD format
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [attendanceDateFilter, setAttendanceDateFilter] = useState(() => {
    // Get local date in YYYY-MM-DD format
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Filter students based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter(student => 
        student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.parentName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    }
  }, [searchTerm, students]);

  // Filter attendance records by selected date
  const getFilteredAttendanceRecords = () => {
    return attendanceRecords.filter(record => record.date === attendanceDateFilter);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching teacher dashboard data...');
      
      // Fetch students (mock data for now)
      const mockStudents: Student[] = [
        {
          id: '1',
          firstName: 'Alex',
          lastName: 'Wilson',
          email: 'student1@example.com',
          gradeLevel: 8,
          status: 'active',
          parentName: 'Robert Wilson',
          parentEmail: 'parent1@example.com',
          enrollmentDate: '2024-09-01',
          programName: 'Robotics Fundamentals'
        },
        {
          id: '2',
          firstName: 'Emma',
          lastName: 'Brown',
          email: 'student2@example.com',
          gradeLevel: 9,
          status: 'active',
          parentName: 'Lisa Brown',
          parentEmail: 'parent2@example.com',
          enrollmentDate: '2024-09-01',
          programName: 'Advanced Coding'
        },
        {
          id: '3',
          firstName: 'James',
          lastName: 'Miller',
          email: 'student3@example.com',
          gradeLevel: 7,
          status: 'active',
          parentName: 'David Miller',
          parentEmail: 'parent3@example.com',
          enrollmentDate: '2024-09-15',
          programName: 'STEM Exploration'
        },
        {
          id: '4',
          firstName: 'Sophia',
          lastName: 'Taylor',
          email: 'student4@example.com',
          gradeLevel: 10,
          status: 'active',
          parentName: 'Jennifer Taylor',
          parentEmail: 'parent4@example.com',
          enrollmentDate: '2024-09-01',
          programName: '3D Printing Workshop'
        }
      ];
      
      setStudents(mockStudents);

      // Load attendance records from localStorage
      const savedRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      
      // If no saved records, use mock data for demonstration
      const attendanceRecords = savedRecords.length > 0 ? savedRecords : [
        {
          id: '1',
          studentName: 'Alex Wilson',
          programName: 'Robotics Fundamentals',
          date: new Date().toISOString().split('T')[0], // Today's date
          status: 'present',
          notes: 'Great participation'
        },
        {
          id: '2',
          studentName: 'Emma Brown',
          programName: 'Advanced Coding',
          date: new Date().toISOString().split('T')[0], // Today's date
          status: 'present',
          notes: 'Excellent work on project'
        }
      ];
      
      setAttendanceRecords(attendanceRecords);

      // Calculate class stats
      const stats: ClassStats = {
        totalStudents: mockStudents.length,
        presentToday: attendanceRecords.filter((a: AttendanceRecord) => a.status === 'present').length,
        absentToday: attendanceRecords.filter((a: AttendanceRecord) => a.status === 'absent').length,
        lateToday: attendanceRecords.filter((a: AttendanceRecord) => a.status === 'late').length,
        averageAttendance: 85, // Mock data
        upcomingSessions: 3
      };
      
      setClassStats(stats);

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendance = async (studentId: string, status: 'present' | 'absent' | 'late', notes?: string) => {
    try {
      const student = students.find(s => s.id === studentId);
      if (!student) {
        setError('Student not found');
        return;
      }

      // Create new attendance record
      const newRecord: AttendanceRecord = {
        id: Date.now().toString(),
        studentName: `${student.firstName} ${student.lastName}`,
        programName: student.programName,
        date: selectedDate,
        status,
        notes
      };
      
      // Update local state
      setAttendanceRecords(prev => [...prev, newRecord]);
      
      // Save to localStorage for persistence
      const existingRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const updatedRecords = [...existingRecords, newRecord];
      localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
      
      setShowAttendanceForm(false);
      setSearchTerm('');
      setSelectedStudent(null);
      
      // Update stats
      if (classStats) {
        setClassStats({
          ...classStats,
          presentToday: status === 'present' ? classStats.presentToday + 1 : classStats.presentToday,
          absentToday: status === 'absent' ? classStats.absentToday + 1 : classStats.absentToday,
          lateToday: status === 'late' ? classStats.lateToday + 1 : classStats.lateToday
        });
      }
      
      console.log('✅ Attendance marked successfully:', newRecord);
    } catch (err: any) {
      console.error('Error marking attendance:', err);
      setError('Failed to mark attendance');
    }
  };

  if (loading) {
    return (
      <div className="teacher-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading teacher dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teacher-dashboard">
        <div className="error-container">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-dashboard">
      <div className="dashboard-header">
        <h1>Teacher Dashboard</h1>
        <p>Welcome back, {user?.email}</p>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          👥 Students
        </button>
        <button 
          className={`nav-tab ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => setActiveTab('attendance')}
        >
          📅 Attendance
        </button>
        <button 
          className={`nav-tab ${activeTab === 'classes' ? 'active' : ''}`}
          onClick={() => setActiveTab('classes')}
        >
          🎓 Classes
        </button>
        <button 
          className={`nav-tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          📈 Reports
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="dashboard-content">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{classStats?.totalStudents || 0}</h3>
                <p>Total Students</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{classStats?.presentToday || 0}</h3>
                <p>Present Today</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">❌</div>
              <div className="stat-content">
                <h3>{classStats?.absentToday || 0}</h3>
                <p>Absent Today</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏰</div>
              <div className="stat-content">
                <h3>{classStats?.lateToday || 0}</h3>
                <p>Late Today</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{classStats?.averageAttendance || 0}%</h3>
                <p>Avg Attendance</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <h3>{classStats?.upcomingSessions || 0}</h3>
                <p>Upcoming Sessions</p>
              </div>
            </div>
          </div>

          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <div className="action-buttons">
              <button 
                className="btn btn-primary"
                onClick={() => setShowAttendanceForm(true)}
              >
                📝 Mark Attendance
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setActiveTab('students')}
              >
                👥 View Students
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setActiveTab('attendance')}
              >
                📅 Attendance Records
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Students Tab */}
      {activeTab === 'students' && (
        <div className="dashboard-content">
          <div className="students-header">
            <h3>My Students</h3>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAttendanceForm(true)}
            >
              📝 Mark Attendance
            </button>
          </div>

          <div className="students-grid">
            {students.map((student) => (
              <div key={student.id} className="student-card">
                <div className="student-info">
                  <h4>{student.firstName} {student.lastName}</h4>
                  <p className="student-email">{student.email}</p>
                  <p className="student-grade">Grade {student.gradeLevel}</p>
                  <p className="student-program">{student.programName}</p>
                </div>
                <div className="student-actions">
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setSelectedStudent(student);
                      setShowAttendanceForm(true);
                    }}
                  >
                    📝 Attendance
                  </button>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => {/* View student details */}}
                  >
                    👁️ View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="dashboard-content">
          <div className="attendance-header">
            <h3>Attendance Records</h3>
            <div className="date-selector">
              <label>Filter by Date:</label>
              <input 
                type="date" 
                value={attendanceDateFilter}
                onChange={(e) => setAttendanceDateFilter(e.target.value)}
                className="date-input"
              />
            </div>
          </div>

          <div className="attendance-summary">
            <p>
              Showing attendance for <strong>{new Date(attendanceDateFilter).toLocaleDateString()}</strong>
              <span className="record-count">({getFilteredAttendanceRecords().length} records)</span>
            </p>
          </div>

          <div className="attendance-table-container">
            {getFilteredAttendanceRecords().length === 0 ? (
              <div className="no-attendance">
                <p>No attendance records found for {new Date(attendanceDateFilter).toLocaleDateString()}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAttendanceForm(true)}
                >
                  📝 Mark Attendance for This Date
                </button>
              </div>
            ) : (
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Program</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredAttendanceRecords().map((record) => (
                    <tr key={record.id}>
                      <td>{record.studentName}</td>
                      <td>{record.programName}</td>
                      <td>{record.date}</td>
                      <td>
                        <span className={`status-badge ${record.status}`}>
                          {record.status}
                        </span>
                      </td>
                      <td>{record.notes || '-'}</td>
                      <td>
                        <button className="btn btn-sm btn-secondary">
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Classes Tab */}
      {activeTab === 'classes' && (
        <div className="dashboard-content">
          <h3>My Classes</h3>
          <div className="classes-grid">
            <div className="class-card">
              <h4>Robotics Fundamentals</h4>
              <p>Grade 8-10 • 12 students</p>
              <p>Mon, Wed, Fri • 3:00 PM - 4:30 PM</p>
              <div className="class-actions">
                <button className="btn btn-primary">📝 Mark Attendance</button>
                <button className="btn btn-secondary">👥 View Students</button>
              </div>
            </div>
            <div className="class-card">
              <h4>Advanced Coding</h4>
              <p>Grade 9-12 • 8 students</p>
              <p>Tue, Thu • 4:00 PM - 5:30 PM</p>
              <div className="class-actions">
                <button className="btn btn-primary">📝 Mark Attendance</button>
                <button className="btn btn-secondary">👥 View Students</button>
              </div>
            </div>
            <div className="class-card">
              <h4>STEM Exploration</h4>
              <p>Grade 6-8 • 15 students</p>
              <p>Mon, Wed • 2:00 PM - 3:30 PM</p>
              <div className="class-actions">
                <button className="btn btn-primary">📝 Mark Attendance</button>
                <button className="btn btn-secondary">👥 View Students</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="dashboard-content">
          <h3>Reports & Analytics</h3>
          <div className="reports-grid">
            <div className="report-card">
              <h4>Attendance Summary</h4>
              <p>View attendance trends and statistics</p>
              <button className="btn btn-secondary">📊 View Report</button>
            </div>
            <div className="report-card">
              <h4>Student Progress</h4>
              <p>Track individual student progress</p>
              <button className="btn btn-secondary">📈 View Report</button>
            </div>
            <div className="report-card">
              <h4>Class Performance</h4>
              <p>Analyze class performance metrics</p>
              <button className="btn btn-secondary">📋 View Report</button>
            </div>
            <div className="report-card">
              <h4>Parent Communication</h4>
              <p>Send updates to parents</p>
              <button className="btn btn-secondary">📧 Send Updates</button>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Form Modal */}
      {showAttendanceForm && (
        <div className="modal-overlay">
          <div className="modal attendance-modal">
            <div className="modal-header">
              <h3>Mark Attendance</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowAttendanceForm(false);
                  setSearchTerm('');
                  setSelectedStudent(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div className="attendance-form">
                <div className="form-group">
                  <label>Search Student:</label>
                  <input 
                    type="text"
                    placeholder="Search by student name, email, or parent name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input search-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Select Student:</label>
                  <div className="student-list">
                    {filteredStudents.length === 0 ? (
                      <div className="no-results">
                        {searchTerm ? 'No students found matching your search.' : 'No students available.'}
                      </div>
                    ) : (
                      filteredStudents.map(student => (
                        <div 
                          key={student.id}
                          className={`student-option ${selectedStudent?.id === student.id ? 'selected' : ''}`}
                          onClick={() => setSelectedStudent(student)}
                        >
                          <div className="student-info">
                            <div className="student-name">
                              {student.firstName} {student.lastName}
                            </div>
                            <div className="student-details">
                              <span className="student-email">{student.email}</span>
                              <span className="student-grade">Grade {student.gradeLevel}</span>
                            </div>
                            <div className="parent-info">
                              Parent: {student.parentName} ({student.parentEmail})
                            </div>
                          </div>
                          {selectedStudent?.id === student.id && (
                            <div className="selected-indicator">✓</div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Date:</label>
                  <input 
                    type="date" 
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="form-input"
                  />
                </div>
                
                {selectedStudent && (
                  <div className="selected-student-info">
                    <h4>Selected Student:</h4>
                    <p><strong>Name:</strong> {selectedStudent.firstName} {selectedStudent.lastName}</p>
                    <p><strong>Parent:</strong> {selectedStudent.parentName}</p>
                    <p><strong>Program:</strong> {selectedStudent.programName}</p>
                  </div>
                )}
                
                <div className="form-group">
                  <label>Status:</label>
                  <div className="status-buttons">
                    <button 
                      className="btn btn-success"
                      onClick={() => selectedStudent && handleMarkAttendance(selectedStudent.id, 'present')}
                      disabled={!selectedStudent}
                    >
                      ✅ Present
                    </button>
                    <button 
                      className="btn btn-warning"
                      onClick={() => selectedStudent && handleMarkAttendance(selectedStudent.id, 'late')}
                      disabled={!selectedStudent}
                    >
                      ⏰ Late
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => selectedStudent && handleMarkAttendance(selectedStudent.id, 'absent')}
                      disabled={!selectedStudent}
                    >
                      ❌ Absent
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDashboard;
